import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  styled,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { NotificationPrompts, TextSizes } from "entities/constants";
import { TextSizeContext } from "data/index";
import RadioButtons from "ui/components/shared/RadioButtons";
import { NotficationSettingInterface, NotificationsTabPropType } from "entities/interfaces";
import WithNotificationsTabData from "ui/containers/settings/NotificationsTabContainer";

// Styled Components
const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "2.5rem",
  padding: "2rem",
  [theme.breakpoints.down("sm")]: {
    padding: "1.5rem 1rem",
  },
}));

const Card = styled(Paper)(({ theme }) => ({
  padding: "2rem",
  borderRadius: "20px",
  boxShadow: theme.shadows[2],
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1.25rem",
  color: theme.palette.text.primary,
}));

const HelperText = styled(Typography)(({ theme }) => ({
  fontStyle: "italic",
  color: theme.palette.text.secondary,
}));

const NotificationsTab = ({
  notificationSettings,
  updateNotificationSettings,
}: NotificationsTabPropType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { state: textSize } = useContext(TextSizeContext);

  const [notification, setNotification] = useState<NotficationSettingInterface>({
    notification1: 0,
    notification2: 0,
    notification3: 0,
  });

  useEffect(() => {
    setNotification(notificationSettings);
  }, [notificationSettings]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: keyof NotficationSettingInterface
  ) => {
    const value = parseInt(event.target.value, 10);
    const updated = { ...notification, [key]: value };
    setNotification(updated);
    updateNotificationSettings(updated);
  };

  return (
    <Container>
      {/* Gentle Prompts */}
      <Card>
        <SectionTitle>{t("Gentle prompts to continue on your path")}</SectionTitle>
        <RadioButtons
          options={[
            t("Frequent"),
            t("Moderate"),
            t("Rare"),
            t("I will never remind you"),
          ]}
          value={notification.notification1}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, "notification1")}
        />
        <HelperText fontSize={TextSizes[textSize].body}>
          {t(NotificationPrompts[notification.notification1])}
        </HelperText>
      </Card>

      {/* New Skills & Updates */}
      <Card>
        <SectionTitle>{t("Get information about new skills and updates")}</SectionTitle>
        <RadioButtons
          options={[t("Yes"), t("No")]}
          value={notification.notification2}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, "notification2")}
        />
      </Card>

      {/* Community Interactions */}
      <Card>
        <SectionTitle>{t("Get information about community interactions")}</SectionTitle>
        <RadioButtons
          options={[t("Yes"), t("No")]}
          value={notification.notification3}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, "notification3")}
        />
      </Card>
    </Container>
  );
};

export default WithNotificationsTabData(NotificationsTab);
